/**
 * SignatureCell — the Signature column of Manual Checkout History.
 *
 * (2026-08-13) History used to render `signatureUrl` (an S3 URL the backend
 * rebuilds from the docNum) and nothing else, so when the server-side upload
 * fails the row shows a broken image with no hint that the signature actually
 * exists. Every ticket looked unsigned even though the tablet still held a copy.
 *
 * Order of preference:
 *   1. the S3 image, when it loads — the server copy is authoritative and is what
 *      the billing report embeds;
 *   2. the copy retained on THIS device (IndexedDB, see utils/signatureStore),
 *      badged so nobody mistakes a device-only image for one the server has;
 *   3. the legacy typed initials, for tickets predating drawn signatures.
 */
import React, { useEffect, useState } from 'react';

export interface SignatureCellProps {
  /** Public S3 URL rebuilt from the docNum; 404s when the upload failed. */
  signatureUrl?: string;
  /** Data URL retained on this device for this ticket, if any. */
  localSignature?: string;
  /** Legacy typed initials, for tickets with no drawn signature at all. */
  initials?: string;
}

export const SignatureCell: React.FC<SignatureCellProps> = ({
  signatureUrl,
  localSignature,
  initials,
}) => {
  const [remoteFailed, setRemoteFailed] = useState(false);
  const [localFailed, setLocalFailed] = useState(false);

  // A new row (or a re-uploaded image) deserves a fresh attempt at the server
  // copy; without this, one 404 would pin the cell to the local image forever.
  useEffect(() => {
    setRemoteFailed(false);
  }, [signatureUrl]);
  useEffect(() => {
    setLocalFailed(false);
  }, [localSignature]);

  const useRemote = !!signatureUrl && !remoteFailed;
  const useLocal = !useRemote && !!localSignature && !localFailed;

  if (!useRemote && !useLocal) {
    return <span className="text-secondary-500">{initials || '—'}</span>;
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <img
        // Keyed by source so swapping S3 → local remounts the <img> rather than
        // reusing the element that just failed to load.
        key={useRemote ? 'remote' : 'local'}
        src={useRemote ? signatureUrl : localSignature}
        alt="Signature"
        onError={() => (useRemote ? setRemoteFailed(true) : setLocalFailed(true))}
        className="h-10 max-w-[140px] object-contain bg-white border border-secondary-200 rounded"
      />
      {useLocal && (
        <span
          className="text-[10px] leading-none px-1 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap"
          title="This image is stored on this tablet only — the server copy is missing. Use Export Signatures to send it for billing."
        >
          on this device
        </span>
      )}
    </div>
  );
};

export default SignatureCell;
